import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  ListChecks,
  Download,
  RefreshCw,
  ExternalLink,
  Leaf,
  AlertTriangle,
  EyeOff,
} from 'lucide-react';
import { Button, Card, LoadingState } from '@/components/common';
import {
  getListaInvestitii,
  descarcaFisierListaInvestitii,
  miiLei,
  leiScurt,
  sumaSurselor,
  surseleNuSeInchid,
  esteCheltuialaVerde,
  textCautare,
  mesajEroareLista,
  numeDescarcare,
  dataLunga,
  dataOra,
} from '@/services/listaInvestitii.service';
import type {
  FisierListaInvestitii,
  ObiectivInvestitie,
  RaspunsListaInvestitii,
  VersiuneListaInvestitii,
  SectiuneInvestitie,
  SursaFinantare,
} from '@/types';

// ============================================================================
// „Lista de investiții" — aceeași anexă pe care o arată dashboard-ul
// administrativ la /pages/lista.php, adusă în aplicația de utilizatori.
//
// Răspunde la „pentru ce s-au alocat bani anul acesta și ce dintre ele urmărim
// efectiv". Un obiectiv de investiții e o LINIE DE BUGET, nu un proiect: în
// revizia r7a, 125 din 152 de obiective nu au proiect în platformă, iar 88
// dintre ele sunt dotări care nici nu vor avea. Pagina arată lista întreagă și
// marchează onest legătura, în loc să amestece lista în ecranul Proiecte.
//
// Cifrele sosesc în lei și se afișează în MII LEI, ca în documentul oficial.
// Filtrarea se face în client: 152 de rânduri încap într-un răspuns, iar o
// cerere pe fiecare tastă apăsată n-ar ajuta pe nimeni.
// ============================================================================

type FiltruProiect = '' | 'are' | 'fara';

// Coloana „Proiect în platformă" e ASCUNSĂ temporar, la cerere (16.09.2026):
// legăturile obiectiv → proiect se mai revizuiesc, așa că nu se arată încă în
// aplicația de utilizatori. În dashboard-ul administrativ (`/pages/lista.php`)
// ea rămâne la vedere — acolo nu s-a schimbat nimic.
//
// Codul coloanei e păstrat întreg, nu șters: se reactivează punând `true`.
// Backendul continuă să trimită legătura (și să o taie pe cea din afara
// portofoliului), deci nu e nimic de refăcut acolo.
const ARATA_COLOANA_PROIECT: boolean = false;

// Numărul de coloane ale tabelului — folosit de rândul-titlu al fiecărui capitol.
const NR_COLOANE = ARATA_COLOANA_PROIECT ? 8 : 7;

export function ListaInvestitiiPage() {
  const [date, setDate] = useState<RaspunsListaInvestitii | null>(null);
  const [seIncarca, setSeIncarca] = useState(true);
  const [eroare, setEroare] = useState<string | null>(null);

  const [cauta, setCauta] = useState('');
  const [capitol, setCapitol] = useState('');
  const [sectiune, setSectiune] = useState<'' | SectiuneInvestitie>('');
  const [filtruProiect, setFiltruProiect] = useState<FiltruProiect>('');

  const [seDescarca, setSeDescarca] = useState(false);
  const [eroareDescarcare, setEroareDescarcare] = useState<string | null>(null);

  const incarca = useCallback(async () => {
    setSeIncarca(true);
    setEroare(null);
    try {
      setDate(await getListaInvestitii());
    } catch (err) {
      console.error('Lista de investiții:', err);
      setEroare(mesajEroareLista(err));
    } finally {
      setSeIncarca(false);
    }
  }, []);

  useEffect(() => {
    incarca();
  }, [incarca]);

  const obiective = date?.obiective ?? [];
  const versiune = date?.versiune ?? null;
  const an = versiune?.an ?? new Date().getFullYear();

  // Textul de căutare se calculează o dată, nu la fiecare tastă.
  const indexCautare = useMemo(() => {
    const m = new Map<number, string>();
    obiective.forEach((o) => m.set(o.id, textCautare(o)));
    return m;
  }, [obiective]);

  const cifre = useMemo(() => {
    let totalAlocat = 0;
    let cuProiect = 0;
    const peSectiune: Record<SectiuneInvestitie, number> = { A: 0, B: 0, C: 0 };
    obiective.forEach((o) => {
      totalAlocat += o.alocatAn;
      peSectiune[o.sectiune]++;
      if (o.legatura !== 'fara') cuProiect++;
    });
    // Lucrările (A și B) fără proiect sunt singura cifră care cere o decizie:
    // dotările din C n-au nevoie de dosar, o lucrare da.
    const faraProiectAB = obiective.filter((o) => o.legatura === 'fara' && o.sectiune !== 'C').length;
    return { totalAlocat, cuProiect, peSectiune, faraProiectAB };
  }, [obiective]);

  const filtrate = useMemo(() => {
    const q = cauta.trim().toLowerCase();
    return obiective.filter((o) => {
      if (capitol && o.capitol !== capitol) return false;
      if (sectiune && o.sectiune !== sectiune) return false;
      if (filtruProiect === 'are' && o.legatura === 'fara') return false;
      if (filtruProiect === 'fara' && o.legatura !== 'fara') return false;
      if (q && !(indexCautare.get(o.id) ?? '').includes(q)) return false;
      return true;
    });
  }, [obiective, cauta, capitol, sectiune, filtruProiect, indexCautare]);

  // Grupare pe capitol, în ordinea în care vin rândurile: backendul le ordonează
  // pe capitol, secțiune și rândul din Excel (cele adăugate din Administrare, la
  // finalul secțiunii lor), deci prima apariție a unui capitol îi dă poziția.
  const grupuri = useMemo(() => {
    const m = new Map<string, ObiectivInvestitie[]>();
    filtrate.forEach((o) => {
      if (!m.has(o.capitol)) m.set(o.capitol, []);
      m.get(o.capitol)!.push(o);
    });
    return Array.from(m.entries());
  }, [filtrate]);

  // Capitolele pentru filtru se iau din lista ÎNTREAGĂ, nu din cea filtrată —
  // altfel alegerea unui capitol ar goli lista de capitole.
  const capitoleDisponibile = useMemo(() => {
    const vazute: string[] = [];
    obiective.forEach((o) => {
      if (!vazute.includes(o.capitol)) vazute.push(o.capitol);
    });
    return vazute;
  }, [obiective]);

  const totalFiltrat = useMemo(
    () => filtrate.reduce((t, o) => t + o.alocatAn, 0),
    [filtrate]
  );

  const descarca = async () => {
    if (!date?.fisier) return;
    setSeDescarca(true);
    setEroareDescarcare(null);
    try {
      await descarcaFisierListaInvestitii(numeDescarcare(date.fisier));
    } catch (err) {
      setEroareDescarcare(err instanceof Error ? err.message : 'Descărcarea nu a reușit.');
    } finally {
      setSeDescarca(false);
    }
  };

  if (seIncarca) {
    return <LoadingState message="Se încarcă lista de investiții..." />;
  }

  const fisier = date?.fisier ?? null;
  // Un capitol introdus din Administrare poate lipsi din nomenclator: se spune, nu se lasă gol.
  const numeCapitol = (cod: string) => date?.capitole?.[cod] ?? 'capitol fără denumire în nomenclator';

  // Toate datele de mai jos trec prin `dataLunga`/`dataOra`, care întorc null
  // pentru null: un `new Date(null)` ar afișa tăcut „1 ianuarie 1970".
  const importatLa = dataLunga(versiune?.importatLa);
  const modificataLa = dataOra(versiune?.modificataLa);
  const verificatLa = dataLunga(fisier?.verificatLa);
  const fisierPusLa = dataLunga(fisier?.modificatLa);
  const fisierIncarcatLa = dataOra(fisier?.incarcatLa);

  return (
    <div className="space-y-6">
      {/* Antet */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <ListChecks className="w-8 h-8 text-primary-600" />
            Lista de investiții {an}
          </h1>
          <p className="text-gray-600 mt-1">
            {versiune
              ? `${versiune.anexa ?? 'Anexa la buget'} — ${versiune.titlu}, revizia ${versiune.cod}`
              : 'Obiectivele de investiții aprobate prin buget'}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button
            variant="secondary"
            leftIcon={<RefreshCw className="w-4 h-4" />}
            onClick={incarca}
          >
            Reîmprospătare
          </Button>
          <Button
            variant="primary"
            leftIcon={<Download className="w-4 h-4" />}
            onClick={descarca}
            isLoading={seDescarca}
            disabled={!fisier?.disponibil}
            title={
              fisier?.disponibil
                ? `Descarcă ${numeDescarcare(fisier)}${fisier.marime ? ` (${(fisier.marime / 1024 / 1024).toFixed(1)} MB)` : ''}`
                : 'Fișierul nu este disponibil pe server'
            }
          >
            Descarcă Excel
          </Button>
        </div>
      </div>

      {eroare && (
        <div className="p-4 bg-danger-50 border border-danger-200 rounded-lg text-danger-700">
          {eroare}
        </div>
      )}
      {eroareDescarcare && (
        <div className="p-4 bg-danger-50 border border-danger-200 rounded-lg text-danger-700">
          {eroareDescarcare}
        </div>
      )}

      {!versiune && !eroare && (
        <Card>
          <p className="text-gray-600">
            Lista de investiții nu a fost încă importată în sistem.
          </p>
        </Card>
      )}

      {versiune && (
        <>
          {/* Ce este lista aceasta — aceeași explicație ca în dashboard-ul administrativ.
              Fără ea, „152 de obiective" lângă „41 de proiecte" pare o contradicție. */}
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-900 max-w-5xl">
            <b>Ce este lista aceasta.</b> Obiectivele de investiții aprobate prin buget
            {' '}({versiune.anexa ?? 'anexa la buget'}, revizia {versiune.cod}), cu banii alocați
            pe {an} și sursele lor. Un obiectiv este o <b>linie de buget</b>, nu un proiect
            {ARATA_COLOANA_PROIECT
              ? ': unde există proiect în platformă, coloana din dreapta duce la el.'
              : '.'}{' '}
            Dotările (secțiunea C) sunt în mare parte achiziții de echipamente și nu au
            nevoie de proiect.
          </div>

          {/* Cifrele se pot edita din Administrare după import. Fără nota asta, cine
              compară pagina cu anexa oficială ar găsi diferențe fără explicație. */}
          {modificataLa && (
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900 max-w-5xl">
              <b>Valori modificate după import.</b> Lista a fost editată din Administrare după
              importul reviziei {versiune.cod} (ultima modificare: {modificataLa}
              {versiune.modificataDe ? `, de ${versiune.modificataDe}` : ''}). Total alocat pe {an}{' '}
              acum: {miiLei(versiune.totalCurent)} mii lei ({versiune.nrObiectiveCurent} obiective),
              față de {miiLei(versiune.totalAn)} mii lei ({versiune.nrObiective} obiective) la import.
            </div>
          )}

          {/* Cifrele de sus */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <CifraCard
              valoare={leiScurt(cifre.totalAlocat)}
              eticheta={`alocat pe ${an} (${miiLei(cifre.totalAlocat)} mii lei)`}
            />
            <CifraCard
              valoare={`${cifre.cuProiect} din ${obiective.length}`}
              eticheta="obiective legate de un proiect din platformă"
            />
            <CifraCard
              valoare={String(cifre.faraProiectAB)}
              eticheta="lucrări (A și B) fără proiect în platformă"
            />
            <CifraCard
              valoare={`${cifre.peSectiune.A} · ${cifre.peSectiune.B} · ${cifre.peSectiune.C}`}
              eticheta="în continuare · noi · alte cheltuieli (A · B · C)"
            />
          </div>

          {/* Filtre */}
          <Card padding="sm">
            <div className="flex flex-wrap gap-3 items-center">
              <input
                type="search"
                value={cauta}
                onChange={(e) => setCauta(e.target.value)}
                placeholder="Caută după denumire, fișă, beneficiar sau proiect…"
                aria-label="Caută în lista de investiții"
                className="flex-1 min-w-[16rem] max-w-lg px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              <select
                value={capitol}
                onChange={(e) => setCapitol(e.target.value)}
                aria-label="Capitol bugetar"
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Toate capitolele</option>
                {capitoleDisponibile.map((c) => (
                  <option key={c} value={c}>
                    {c} {numeCapitol(c)}
                  </option>
                ))}
              </select>
              <select
                value={sectiune}
                onChange={(e) => setSectiune(e.target.value as '' | SectiuneInvestitie)}
                aria-label="Secțiune"
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Toate secțiunile</option>
                {(Object.entries(date?.sectiuni ?? {}) as [string, string][]).map(([cod, den]) => (
                  <option key={cod} value={cod}>
                    {cod}. {den}
                  </option>
                ))}
              </select>
              <select
                value={filtruProiect}
                onChange={(e) => setFiltruProiect(e.target.value as FiltruProiect)}
                aria-label="Legătura cu proiectele"
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Cu și fără proiect</option>
                <option value="are">Doar cu proiect</option>
                <option value="fara">Doar fără proiect</option>
              </select>
            </div>
            <p className="mt-2 text-sm text-gray-500" role="status">
              Afișate: {filtrate.length} {filtrate.length === 1 ? 'obiectiv' : 'obiective'} · alocat {an}:{' '}
              {miiLei(totalFiltrat)} mii lei
            </p>
          </Card>

          {/* Tabelul */}
          <Card padding="none" className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th scope="col" className="text-left py-3 px-3 font-semibold text-gray-700 whitespace-nowrap">Fișa</th>
                    <th scope="col" className="text-left py-3 px-3 font-semibold text-gray-700">Obiectiv de investiții</th>
                    <th
                      scope="col"
                      className="text-center py-3 px-2 font-semibold text-gray-700"
                      title="A · lucrări în continuare, B · lucrări noi, C · alte cheltuieli"
                    >
                      Secț.
                    </th>
                    <th scope="col" className="text-right py-3 px-3 font-semibold text-gray-700 whitespace-nowrap">Valoare totală<br />mii lei</th>
                    <th scope="col" className="text-right py-3 px-3 font-semibold text-gray-700 whitespace-nowrap">Rest la 01.01.{an}<br />mii lei</th>
                    <th scope="col" className="text-right py-3 px-3 font-semibold text-gray-700 whitespace-nowrap">Alocat {an}<br />mii lei</th>
                    <th scope="col" className="text-left py-3 px-3 font-semibold text-gray-700">Surse {an}</th>
                    {ARATA_COLOANA_PROIECT && (
                      <th scope="col" className="text-left py-3 px-3 font-semibold text-gray-700">Proiect în platformă</th>
                    )}
                  </tr>
                </thead>
                {grupuri.map(([cod, lista]) => {
                  const sumaCapitol = lista.reduce((t, o) => t + o.alocatAn, 0);
                  return (
                    <tbody key={cod} className="divide-y divide-gray-100">
                      <tr className="bg-primary-50/60">
                        <th colSpan={NR_COLOANE} scope="colgroup" className="text-left py-2.5 px-3 font-semibold text-primary-800">
                          Cap. {cod} · {numeCapitol(cod)}
                          <span className="ml-3 font-normal text-gray-600">
                            {lista.length} {lista.length === 1 ? 'obiectiv' : 'obiective'} · alocat{' '}
                            {miiLei(sumaCapitol)} mii lei
                          </span>
                        </th>
                      </tr>
                      {lista.map((o) => (
                        <RandObiectiv key={o.id} o={o} numeSursa={(s) => date?.surse?.[s] ?? s} />
                      ))}
                    </tbody>
                  );
                })}
              </table>
            </div>
            {filtrate.length === 0 && (
              <p className="py-8 text-center text-gray-500" role="status">
                Niciun obiectiv nu se potrivește filtrelor.
              </p>
            )}
          </Card>

          {/* Proveniența — de unde vin cifrele și cât de mult se poate conta pe ele. */}
          <div className="text-xs text-gray-500 leading-relaxed max-w-5xl">
            Sursa: {versiune.anexa ?? ''} — {versiune.titlu}, revizia {versiune.cod}
            {' '}(fișierul „{versiune.fisierNume}")
            {importatLa && <>, importată la {importatLa}</>}
            . La import, totalul pe {an} ({miiLei(versiune.totalAn)} mii lei) și subtotalurile pe
            capitole au fost verificate față de rândurile din Excel.
            {modificataLa && ' Valorile au fost modificate ulterior din Administrare (vezi nota de mai sus).'}
            {' '}Legătura cu proiectele e stabilită manual și poate fi corectată.
            {fisier?.disponibil && <ProvenientaFisier fisier={fisier} versiune={versiune} verificatLa={verificatLa} fisierPusLa={fisierPusLa} fisierIncarcatLa={fisierIncarcatLa} valoriModificate={!!modificataLa} />}
          </div>
        </>
      )}
    </div>
  );
}

// Ce se poate spune cinstit despre fișierul de la „Descarcă Excel". Trei stări,
// explicite, nu un ternar pe `verificatPentruRevizia === versiune.cod`: pentru un
// fișier încărcat ambele câmpuri de verificare sunt null, comparația iese false
// și s-ar ajunge în ramura de avertisment cu o revizie goală în frază.
function ProvenientaFisier({
  fisier,
  versiune,
  verificatLa,
  fisierPusLa,
  fisierIncarcatLa,
  valoriModificate,
}: {
  fisier: FisierListaInvestitii;
  versiune: VersiuneListaInvestitii;
  verificatLa: string | null;
  fisierPusLa: string | null;
  fisierIncarcatLa: string | null;
  valoriModificate: boolean;
}) {
  // 1. Fișier încărcat din Administrare: nu a trecut prin verificarea din 16.09,
  //    iar cifrele din tabel NU se citesc din el — deci nu se promite potrivirea.
  if (fisier.sursa === 'incarcat') {
    return (
      <>
        {' '}Butonul „Descarcă Excel" servește fișierul „{numeDescarcare(fisier)}", încărcat
        {fisier.incarcatDe && <> de {fisier.incarcatDe}</>}
        {fisierIncarcatLa && <> la {fisierIncarcatLa}</>}. Cifrele din tabel se actualizează
        separat, din Administrare, deci pot diferi de fișier.
      </>
    );
  }

  const intro = (
    <>
      {' '}Butonul „Descarcă Excel" servește registrul oficial întreg („{fisier.nume}", două foi),
      în forma lui originală.
    </>
  );
  const pusLa = fisierPusLa && <> Fișier pus pe server la {fisierPusLa}.</>;

  // 2. Registrul inițial, verificat pentru revizia afișată.
  if (fisier.verificatPentruRevizia === versiune.cod && verificatLa) {
    return (
      <>
        {intro}
        {' '}Conținutul lui a fost verificat rând cu rând față de revizia {versiune.cod} la{' '}
        {verificatLa}
        {valoriModificate
          ? ', înainte de modificările ulterioare din tabel: cifrele afișate acum pot diferi de fișier.'
          : ': aceleași obiective, aceleași sume.'}
        {pusLa}
      </>
    );
  }

  // 3. Registrul inițial, dar verificat pentru altă revizie (sau neverificat).
  //    Fișierul e ținut separat de bază, deci se pot depărta: o revizie nouă
  //    importată fără înlocuirea fișierului ar face din promisiunea de mai sus o
  //    minciună. Aici pagina o spune singură.
  return (
    <>
      {intro}
      <strong className="text-amber-700">
        {' '}Atenție:{' '}
        {fisier.verificatPentruRevizia
          ? <>conținutul lui a fost verificat pentru revizia {fisier.verificatPentruRevizia}, iar tabelul de mai sus arată revizia {versiune.cod}.</>
          : <>conținutul lui nu a fost verificat față de revizia {versiune.cod} din tabel.</>}
        {' '}Cifrele din fișier pot să difere de cele afișate.
      </strong>
      {pusLa}
    </>
  );
}

function CifraCard({ valoare, eticheta }: { valoare: string; eticheta: string }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <p className="text-2xl font-bold text-gray-900 leading-tight">{valoare}</p>
      <p className="text-sm text-gray-500 mt-1">{eticheta}</p>
    </div>
  );
}

function RandObiectiv({
  o,
  numeSursa,
}: {
  o: ObiectivInvestitie;
  numeSursa: (s: SursaFinantare) => string;
}) {
  const surse = Object.entries(o.surse) as [SursaFinantare, number][];
  const meta = [o.beneficiar, o.subgrup].filter(Boolean) as string[];

  return (
    <tr className="hover:bg-gray-50 align-top">
      <td className="py-2.5 px-3 font-mono text-xs text-gray-500 whitespace-nowrap">{o.nrFisa}</td>
      <td className="py-2.5 px-3">
        <div className="text-gray-900 max-w-2xl">{o.denumire}</div>
        {(meta.length > 0 || esteCheltuialaVerde(o) || o.adaugatManual) && (
          <div className="text-xs text-gray-400 mt-0.5 flex items-center gap-1.5 flex-wrap">
            {meta.length > 0 && <span>{meta.join(' · ')}</span>}
            {esteCheltuialaVerde(o) && (
              <span className="inline-flex items-center gap-1 text-green-600">
                <Leaf className="w-3 h-3" /> cheltuială verde
              </span>
            )}
            {/* Rândul nu există în Excel: cine caută fișa în anexa oficială n-o va găsi. */}
            {o.adaugatManual && (
              <span className="text-gray-500 italic" title="Rând adăugat din Administrare, nu provine din fișierul Excel">
                adăugat manual
              </span>
            )}
          </div>
        )}
      </td>
      <td className="py-2.5 px-2 text-center font-semibold text-gray-500">{o.sectiune}</td>
      <td className="py-2.5 px-3 text-right tabular-nums whitespace-nowrap text-gray-600">
        {miiLei(o.valoareTotala)}
      </td>
      <td className="py-2.5 px-3 text-right tabular-nums whitespace-nowrap text-gray-600">
        {miiLei(o.restExecutat)}
      </td>
      <td className="py-2.5 px-3 text-right tabular-nums whitespace-nowrap font-semibold text-gray-900">
        {miiLei(o.alocatAn)}
      </td>
      <td className="py-2.5 px-3 text-xs text-gray-600 whitespace-nowrap">
        {surse.length === 0 ? (
          '—'
        ) : surse.length === 1 ? (
          numeSursa(surse[0][0])
        ) : (
          surse.map(([cheie, suma]) => (
            <div key={cheie}>
              {numeSursa(cheie)} {miiLei(suma)}
            </div>
          ))
        )}
        {surseleNuSeInchid(o) && (
          <div className="mt-0.5 flex items-start gap-1 text-danger-600">
            <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
            <span>În Excel, sursele însumează {miiLei(sumaSurselor(o))}, diferit de total.</span>
          </div>
        )}
      </td>
      {ARATA_COLOANA_PROIECT && (
      <td className="py-2.5 px-3">
        {o.legatura === 'vizibil' && o.proiectId !== null ? (
          <Link
            to={`/projects/${o.proiectId}`}
            className="inline-flex items-start gap-1 text-primary-600 hover:text-primary-700 hover:underline font-medium"
          >
            <span>{o.proiectNume}</span>
            <ExternalLink className="w-3 h-3 mt-1 flex-shrink-0" />
          </Link>
        ) : o.legatura === 'ascuns' ? (
          <span className="inline-flex items-center gap-1 text-xs text-gray-400">
            <EyeOff className="w-3 h-3" /> în evidență (în afara portofoliului dvs.)
          </span>
        ) : (
          <span className="text-xs text-amber-600">fără proiect</span>
        )}
        {o.legaturaNota && <div className="text-xs text-gray-400 mt-0.5">{o.legaturaNota}</div>}
      </td>
      )}
    </tr>
  );
}
